import Node from 'nanogl-node'

import { clamp01, mix, randomInt } from '@/webgl/math'
import { easeInCubic } from '@/webgl/math/Ease'
import { SpawnCollectibleEvent } from '@/webgl/runner/core/GameEvents'
import { BonusType } from '@/webgl/runner/entities/BonusEntity'
import {
  CollectibleEntity,
  CollectibleSwitch,
  CollectStatus,
  getCollectibleSwitchFromBonus,
} from '@/webgl/runner/entities/CollectibleEntity'
import { TimeEntity } from '@/webgl/runner/entities/Time'
import GameWorld from '@/webgl/runner/GameWorld'
import { ISystem } from '@/webgl/runner/interfaces/ISystem'
import { SequenceAlternativeMode } from '@/webgl/runner/scenarios/SequenceConfig.types'

export default class CollectibleSystem implements ISystem {
  public autoSpawn = false
  private spawnTime: number = 0
  private nextSpawn: number = 0

  constructor(readonly world: GameWorld) {
    this.world.events.SpawnCollectible.on(this.spwanCollectible)
  }

  dispose() {
    this.world.events.SpawnCollectible.off(this.spwanCollectible)
  }

  spwanCollectible = (evt: SpawnCollectibleEvent) => {
    const { settings, registry } = this.world

    if (registry.collectibles.count() == 0) return
    const data = registry.collectibles.get(evt.id)

    const uuid = evt.entity?.uuid ?? crypto.randomUUID()

    const rrange = 0.05
    const node = new Node()
    node.setScale(0)
    node.z = -settings.LANE_LENGTH + (evt.offset ?? 0)
    node.x = evt.lane * settings.LANE_SIZE
    node.rotateX(0.1)
    node.rotateZ(Math.random() * rrange - rrange * 0.5)

    if (data.canSwitch) {
      node.y += 0.15
    }

    // Switch
    let cswitch: CollectibleSwitch = null
    for (const bonus of this.world.bonuses) {
      cswitch = getCollectibleSwitchFromBonus(bonus.type, 1.0) || cswitch
    }

    if (this.world.isBonusActive(BonusType.Switch)) {
      node.rotateZ(Math.random() * Math.PI * 2)
    }

    // Blur
    const blur = this.world.isBonusActive(BonusType.Helmet) ? 1 : 0

    // View variants
    let variant = evt.entity?.variant ?? undefined
    if (data.variants && data.variants.length > 0) {
      variant = data.variants[Math.floor(Math.random() * data.variants.length)]
    }

    // Price
    let priceID = evt.entity?.priceID ?? undefined
    if (data.prices && data.prices.length > 0 && priceID == undefined) {
      priceID = evt.priceID !== undefined ? evt.priceID : Math.floor(Math.random() * data.prices.length)
    }

    // Mirror
    const mirrored = this.world.isBonusActive(BonusType.Mirror)

    const entity: CollectibleEntity = {
      node,
      uuid,
      y: node.y,
      scale: 0.4,
      lane: evt.lane,
      seed: Math.random(),
      spawntime: this.world.time.unscaledTime,
      lifetime: 0,
      releasing: false,
      collected: false,
      collectedTime: 0,
      collectStatus: CollectStatus.Neutral,
      highlight: false,
      data,
      switch: data.canSwitch ? cswitch : null,
      blur,
      variant,
      priceID,
      mirrored,
    }

    this.world.root.add(entity.node)
    this.world.collectibles.push(entity)
  }

  spawnRandom() {
    const { settings, registry: assets } = this.world

    if (assets.collectibles.count() == 0) return

    const data = assets.collectibles.getRandomCollectible()

    const hcount = Math.floor(settings.LANE_COUNT / 2)
    const lane = randomInt(-hcount, hcount)

    this.spwanCollectible({ id: data.id, lane })
    this.nextSpawn = 1.5 + Math.random() * 1.5
  }

  updateSwitch(entity: CollectibleEntity, deltaTime: number) {
    if (!entity.switch) return

    const settings = this.world.settings
    let dt = deltaTime * entity.switch.speed
    if (entity.switch.unwrap && entity.node.z > settings.GRAB_THRESHOLD - 2.0) dt *= -1

    entity.switch.progress += dt
    entity.switch.progress = clamp01(entity.switch.progress)

    entity.switch.apply = entity.switch.progress >= 0.5
    entity.node.setScale(entity.scale - Math.sin(entity.switch.progress * Math.PI) * entity.scale)
  }

  public update(time: TimeEntity) {
    const settings = this.world.settings
    const player = this.world.player

    const goal = this.world.state.getCurrentGoal()
    const goals = this.world.state.getCurrentGoals()
    const mode = this.world.state.getAlternativeMode()
    const currentblock = this.world.state.getCurrentCompletionBlock()

    let progression: string[] = []
    if (currentblock) progression = currentblock.collected

    const deltaTime = time.unscaledDeltaTime

    if (this.autoSpawn) {
      this.spawnTime += deltaTime * settings.scrollSpeed

      if (this.spawnTime > this.nextSpawn) {
        this.spawnRandom()
        this.spawnTime = 0
      }
    }

    const highlightBonus = this.world.isBonusActive(BonusType.Focus)
    const blurBonus = this.world.isBonusActiveAny([BonusType.Helmet, BonusType.AltPrice, BonusType.Freeze])
    const rotateBonus = this.world.isBonusActive(BonusType.Switch)

    const destroyable = []
    const collected = []

    for (const entity of this.world.collectibles) {
      if (entity.collected) {
        collected.push(entity)
        continue
      }

      entity.highlight = false
      if (highlightBonus) {
        const blockcollected = progression.includes(entity.data.id)
        entity.highlight = entity.data.id == goal && !blockcollected
        if (mode == SequenceAlternativeMode.Unordered) {
          entity.highlight = goals.includes(entity.data.id) && !blockcollected
        }
      }

      entity.node.z += deltaTime * settings.scrollSpeed
      entity.lifetime += deltaTime

      const p = 1.0 - Math.abs(entity.node.z + 3) / settings.LANE_LENGTH
      entity.node.y = entity.y + (1.0 - p) * 0.1

      if (entity.highlight) entity.node.y += Math.sin(time.unscaledTime * 7) * 0.04

      const scale = entity.scale * p
      entity.node.setScale(scale)
      if (entity.mirrored) entity.node.scale[0] = -scale

      this.updateSwitch(entity, deltaTime)

      if (rotateBonus && entity.data.canSwitch) entity.node.rotateZ(deltaTime * 0.75 * Math.sign(entity.seed - 0.5))

      if (blurBonus) {
        entity.blur = clamp01(entity.blur + deltaTime * 2)
      } else {
        entity.blur = clamp01(entity.blur - deltaTime * 2)
      }

      if (entity.node.z > settings.GRAB_THRESHOLD && entity.lane == player.lane) {
        this.world.events.CollectibleGrabbed.emit(entity)
      }

      if (entity.node.z > settings.MISS_THRESHOLD) {
        this.world.events.CollectibleMissed.emit(entity)
        destroyable.push(entity)
      }
    }

    for (const entity of collected) {
      const duration = 0.4
      entity.lifetime += time.unscaledDeltaTime
      const t = entity.lifetime - entity.collectedTime
      const phase = easeInCubic(t / duration)

      if (entity.collectStatus == CollectStatus.Succeed) {
        entity.node.y = entity.y + Math.sin(phase * Math.PI) * 0.1
        entity.node.setScale(entity.scale * (1.0 - phase))
        entity.node.x = mix(entity.lane * settings.LANE_SIZE, player.node.x, phase)
      }

      if (entity.collectStatus == CollectStatus.Failed) {
        entity.node.x = entity.lane * settings.LANE_SIZE + Math.sin(t * 50) * 0.01
        entity.node.y = entity.y + phase * 0.15
        entity.node.setScale(entity.scale * (1.0 - phase))
      }

      entity.releasing = t > 0.2

      if (t > duration) {
        destroyable.push(entity)
      }
    }

    for (const entity of destroyable) {
      this.world.destroyCollectible(entity)
    }
  }
}
